import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const INTEREST_OPTIONS = [
  { key: "waldorf", label: "Waldorf pedagojisi" },
  { key: "doga_egitimi", label: "Doğa eğitimi" },
  { key: "ev_okulu", label: "Ev okulu" },
  { key: "el_isi", label: "El işi / keçe / ahşap" },
  { key: "mevsimsel_ritim", label: "Mevsimsel ritim" },
  { key: "ogretmen", label: "Öğretmenim" },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    city: "",
    interests: [],
    consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const toggleInterest = (key) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(key) ? f.interests.filter((k) => k !== key) : [...f.interests, key],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setError("Devam etmek için KVKK aydınlatma metnini kabul etmelisin.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await register({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        city: form.city.trim(),
        interests: form.interests,
      });
      toast.success("Hoş geldin, Kabile'ye katıldığın için mutluyuz! 🌱");
      navigate("/forum", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Kayıt oluşturulamadı");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="k-container py-16 md:py-20">
      <div className="mx-auto max-w-xl">
        <h1 className="font-display text-6xl text-ink-900 text-center" data-testid="register-title">
          Kabileye katıl
        </h1>
        <p className="mt-2 text-center font-body text-ink-700">
          Ücretsiz. Birkaç adımda topluluğun bir parçası olursun.
        </p>

        <div className="k-card mt-8 p-8">
          <form onSubmit={submit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="k-label">İsim</label>
              <input
                data-testid="register-name-input"
                required
                minLength={2}
                className="k-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Adın soyadın"
              />
            </div>
            <div>
              <label className="k-label">E-posta</label>
              <input
                data-testid="register-email-input"
                type="email"
                required
                className="k-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="sen@ornek.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="k-label">Şifre</label>
              <input
                data-testid="register-password-input"
                type="password"
                required
                minLength={6}
                className="k-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="En az 6 karakter"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="k-label">Şehir (opsiyonel)</label>
              <input
                data-testid="register-city-input"
                className="k-input"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="İstanbul, İzmir, Ankara…"
              />
            </div>

            <div>
              <label className="k-label">İlgi alanların</label>
              <div className="flex flex-wrap gap-2" data-testid="register-interests">
                {INTEREST_OPTIONS.map((o) => {
                  const active = form.interests.includes(o.key);
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => toggleInterest(o.key)}
                      data-testid={`interest-${o.key}`}
                      className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                        active
                          ? "border-sage-500 bg-sage-500 text-cream-50"
                          : "border-cream-300 bg-cream-50 text-ink-700 hover:border-sage-500/60"
                      }`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="flex items-start gap-3 rounded-xl bg-cream-200/50 p-4 text-sm text-ink-700">
              <input
                type="checkbox"
                data-testid="register-consent"
                checked={form.consent}
                onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                className="mt-1 h-4 w-4 accent-sage-500"
              />
              <span>
                KVKK kapsamında kişisel verilerimin (ad, e-posta, şehir, ilgi alanları) topluluk işleyişi için
                işlenmesine onay veriyorum. Bilgilerim üçüncü taraflarla paylaşılmaz.
              </span>
            </label>

            {error && (
              <div
                className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="k-btn-primary w-full disabled:opacity-60"
              data-testid="register-submit-btn"
            >
              {busy ? "Kaydediliyor…" : "Kabileye katıl"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-ink-500">
            <div className="k-divider" />
            veya
            <div className="k-divider" />
          </div>

          <button
            onClick={startGoogleLogin}
            className="w-full rounded-full border border-cream-300 bg-cream-50 px-6 py-3 font-semibold text-ink-900 transition-colors hover:bg-cream-200"
            data-testid="google-register-btn"
          >
            Google ile katıl
          </button>

          <p className="mt-6 text-center font-body text-sm text-ink-700">
            Zaten üye misin?{" "}
            <Link to="/login" className="font-bold text-sage-700 hover:underline" data-testid="register-to-login">
              Giriş yap
            </Link>
          </p>
          <p className="mt-3 text-center font-body text-xs text-ink-500">
            Sorular için:{" "}
            <a href="mailto:kabilewaldorf@gmail.com" className="text-sage-700 hover:underline">
              kabilewaldorf@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
